import React, { useEffect, useRef, useState } from "react";
import usePrefersReducedMotion from "@/hooks/usePrefersReducedMotion";

/**
 * ParallaxCard — subtle vertical parallax on an image inside a card.
 * Meant for editorial grids (e.g. triptych) — the image drifts slightly
 * against the surrounding text as the user scrolls, adding depth without
 * disrupting the reading rhythm. Auto-disables on prefers-reduced-motion.
 */
export default function ParallaxCard({
  src,
  alt = "",
  aspect = "aspect-[3/4]",
  strength = 0.12,
  className = "",
  imgClassName = "",
  filter = "grayscale(1) contrast(1.15) brightness(0.92) saturate(0)",
  hoverColor = false,
  "data-testid": testId,
}) {
  const ref = useRef(null);
  const [offset, setOffset] = useState(0);
  const [hover, setHover] = useState(false);
  const reduced = usePrefersReducedMotion();

  useEffect(() => {
    if (reduced) return;
    let raf = 0;
    const onScroll = () => {
      if (raf) return;
      raf = requestAnimationFrame(() => {
        raf = 0;
        const el = ref.current;
        if (!el) return;
        const rect = el.getBoundingClientRect();
        const vh = window.innerHeight;
        const centered = (rect.top + rect.height / 2 - vh / 2) / vh;
        setOffset(-centered * strength * 100);
      });
    };
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => {
      window.removeEventListener("scroll", onScroll);
      if (raf) cancelAnimationFrame(raf);
    };
  }, [strength, reduced]);

  return (
    <div
      ref={ref}
      data-testid={testId}
      onMouseEnter={hoverColor ? () => setHover(true) : undefined}
      onMouseLeave={hoverColor ? () => setHover(false) : undefined}
      className={`relative overflow-hidden bg-[#0A0A08] ${aspect} ${className}`}
    >
      <img
        src={src}
        alt={alt}
        className={`w-full h-full object-cover ${imgClassName}`}
        style={{
          transform: reduced
            ? "none"
            : `translate3d(0, ${offset}px, 0) scale(1.12)`,
          transition: "transform 80ms linear, filter 900ms cubic-bezier(0.19,1,0.22,1)",
          filter: hoverColor && hover ? "grayscale(0) contrast(1) brightness(1) saturate(1)" : filter,
          willChange: reduced ? "auto" : "transform",
        }}
      />
    </div>
  );
}
